import { Empty, Skeleton } from 'antd'
import CantileverCard from 'components/CantileverCard'
import {
  DocumentList,
  DocumentPreview,
  RolodexDocumentEntry,
  useLinkedDocuments,
} from 'features/files'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { GeckoEntry, RolodexEntryTagsType } from 'types'

const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const DocumentListWrapper = styled.div`
  max-height: 18rem;
  overflow-y: auto;
`

interface FilePreviewPanelProps {
  entry: GeckoEntry
}

export const FilePreviewPanel: React.FC<FilePreviewPanelProps> = ({
  entry,
}) => {
  const [selectedDocument, setSelectedDocument] =
    useState<RolodexDocumentEntry | null>(null)

  const linkedDocumentTags = useMemo<RolodexEntryTagsType>(
    () => ({
      [entry.type]: entry.id,
    }),
    [entry.id, entry.type]
  )

  const { documents, isLoading } = useLinkedDocuments(linkedDocumentTags)

  const handleDocumentSelect = useCallback(
    (documentId: string) => {
      const document = documents.find((doc) => doc.entry_id === documentId)
      if (document) {
        // Unselect if clicking on the row already selected
        if (document.entry_id === selectedDocument?.entry_id) {
          setSelectedDocument(null)
          return
        }
        setSelectedDocument(document)
      }
    },
    [documents, selectedDocument]
  )

  return (
    <CantileverCard>
      <CantileverCard.CardHeader
        leadingContent='File Preview'
        subContent={
          documents.length > 0 ? `${documents.length} files` : undefined
        }
      />
      <CantileverCard.Section topPadding bottomPadding>
        {isLoading ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : documents.length === 0 ? (
          <Empty description='No files present' />
        ) : (
          <PreviewContainer>
            <DocumentListWrapper>
              <DocumentList
                documents={documents}
                selectedDocumentId={selectedDocument?.entry_id || null}
                onDocumentSelect={handleDocumentSelect}
              />
            </DocumentListWrapper>
            {selectedDocument && (
              <DocumentPreview document={selectedDocument} />
            )}
          </PreviewContainer>
        )}
      </CantileverCard.Section>
    </CantileverCard>
  )
}
